/**
 * DungeonWorldLayer (O-025) — the techdemo-first, client-local integration of the
 * standalone procedural-dungeons package (features/procedural-dungeons).
 *
 * SCOPE (owner decision, 2026-07-19): validate + prove in the TECHDEMO environment.
 * Everything here is presentation-side and client-local — NOTHING on the wire, no
 * shared-sim/tick-path/schema change. Server-authoritative instances, persistence
 * and rewards remain the documented deferred stories (docs/features/*.md §12/§14).
 *
 * Enabled only by the `?dungeons=1` query flag (off in normal playtest). The layer:
 *  - places the deterministic entrance sites for the fixed techdemo world seed;
 *  - runs the package's pure observer lifecycle against the LOCAL player position
 *    (generate at 180 m, reveal omens at 120 m, reset hysteresis at 240 m);
 *  - renders each shrine from its renderer-neutral scene plan (difficulty glow
 *    ladder; strong portal-door glow radiating the area glow);
 *  - shows the three owner-rule omen markers above the portal;
 *  - on interact (G) inside the gold ring, enters a client-local interior preview
 *    assembled from `dungeon-interior-kit-v1` via the existing AssetLoader.
 */

import * as THREE from 'three';

import {
  DEFAULT_DUNGEON_ENTRANCE_RADII,
  placeDungeonEntranceSites,
  updateDungeonEntranceLifecycle,
  planDungeonEntranceScene,
  type DungeonCatalog,
  type DungeonEnemyIconRule,
  type DungeonEntranceAnchor,
  type DungeonEntranceRuntimeState,
  type DungeonEntranceSite,
  type DungeonShrineProfile,
} from '@great-cloud/procedural-dungeons';

import { ShrineVisual, shrineModelSource } from './shrineRenderer.js';
import { DungeonExplorer, type InteriorPrecompile } from './explorer.js';
import type { LoaderFor } from './interior.js';
import type { PreviewSpot } from './previewSpot.js';
import { isGameKey } from '../keyTarget.js';
import { markWorldContent } from '../scene/worldContent.js';
import { withoutServerSites } from './run.js';
import bundled from './catalog.json' with { type: 'json' };

/** Fixed, versioned techdemo world seed — same value every boot ⇒ same shrine map. */
export const TECHDEMO_WORLD_SEED = 'tgc-techdemo-world-v1';

/**
 * Party size the layer asks the generator for (AD-404 §7 — stated, not implied).
 * The techdemo layer is client-local and has exactly one observer, the local player:
 * there is no party on this path to read a size from, and chambers are sized from
 * it (§4.1), so the preview default is 1 until a party-aware entry exists.
 */
export const DUNGEON_PREVIEW_PARTY_SIZE = 1;

interface Bundle {
  catalog: DungeonCatalog;
  iconRules: DungeonEnemyIconRule[];
  shrineProfiles: DungeonShrineProfile[];
  anchors: DungeonEntranceAnchor[];
}

const BUNDLE = bundled as unknown as Bundle;

/**
 * The ONE generated catalogue (the server reads this same file, AD-702) — the
 * networked run rebuilds the server's plan against it (`runClient.ts`).
 */
export const DUNGEON_CATALOG: DungeonCatalog = BUNDLE.catalog;

export function dungeonsEnabled(search: string): boolean {
  try {
    return new URLSearchParams(search).get('dungeons') === '1';
  } catch {
    return false;
  }
}

export class DungeonWorldLayer {
  readonly group = new THREE.Group();
  private readonly sites: DungeonEntranceSite[];
  private readonly states = new Map<string, DungeonEntranceRuntimeState>();
  private readonly visuals = new Map<string, ShrineVisual>();
  private readonly explorer: DungeonExplorer;
  private readonly zoneId: string;
  private interactSiteId: string | null = null;
  private disposed = false;
  private prompt: HTMLDivElement | null = null;
  private readonly onInteractKey = (event: KeyboardEvent): void => {
    if (event.code !== 'KeyG' || this.explorer.active || this.interactSiteId === null) return;
    if (!isGameKey(event)) return; // a "g" typed into chat is not an entry
    if (this.tryEnter()) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    loaderFor: LoaderFor,
    zoneId: string,
    private readonly playerId: string,
    precompile: InteriorPrecompile | null = null,
    serverSites: readonly { x: number; z: number; reach: number }[] = [],
  ) {
    this.zoneId = zoneId;
    this.group.name = 'dungeon-entrances';
    scene.add(markWorldContent(this.group)); // AD-705: the world's entrances, hidden in a run
    this.explorer = new DungeonExplorer(scene, camera, loaderFor, BUNDLE.catalog, precompile);
    // Deterministic placement: one site per difficulty across the authored anchors,
    // filtered to THIS zone (each zone renders only its own shrines).
    const placed = placeDungeonEntranceSites(
      {
        worldSeed: TECHDEMO_WORLD_SEED,
        difficultyQuotas: [
          { difficultyId: 'story', count: 1 },
          { difficultyId: 'adventurer', count: 2 },
          { difficultyId: 'veteran', count: 2 },
          { difficultyId: 'mythic', count: 1 },
        ],
      },
      BUNDLE.anchors,
      BUNDLE.shrineProfiles,
      BUNDLE.catalog,
    );
    this.sites = withoutServerSites(
      placed.filter((site) => site.biomeId === zoneId),
      serverSites,
    );
    for (const site of this.sites) {
      this.states.set(site.id, { phase: 'dormant', siteId: site.id, generationCycle: 0 });
      const profile = BUNDLE.shrineProfiles.find(
        (entry) => entry.difficultyId === site.difficultyId,
      ) as DungeonShrineProfile;
      const visual = new ShrineVisual(site, profile, shrineModelSource(loaderFor));
      this.visuals.set(site.id, visual);
      this.group.add(visual.group);
      this.refreshVisual(site.id);
    }
    window.addEventListener('keydown', this.onInteractKey, true);
  }

  /** True while the interior preview owns movement — the game swallows move input. */
  blocksGameInput(): boolean {
    return this.explorer.active;
  }

  /** Nearest primed site whose gold ring contains the player, else null. */
  currentInteractHint(): string | null {
    return this.interactSiteId;
  }

  /** Per-frame: lifecycle vs the local observer, glow animation, interact detection. */
  frame(dtMs: number, playerX: number, playerZ: number, nowMs: number): void {
    if (this.disposed) return;
    if (this.explorer.active) {
      this.explorer.frame(dtMs);
      return;
    }
    for (const site of this.sites) {
      const state = this.states.get(site.id) as DungeonEntranceRuntimeState;
      const result = updateDungeonEntranceLifecycle({
        worldSeed: TECHDEMO_WORLD_SEED,
        site,
        state,
        observers: [
          {
            playerId: this.playerId,
            position: { x: playerX, y: 0, z: playerZ },
            partySize: DUNGEON_PREVIEW_PARTY_SIZE,
          },
        ],
        catalog: BUNDLE.catalog,
        shrineProfiles: BUNDLE.shrineProfiles,
        iconRules: BUNDLE.iconRules,
      });
      if (result.state !== state) {
        this.states.set(site.id, result.state);
        if (result.events.length > 0) this.refreshVisual(site.id);
      }
      this.visuals.get(site.id)?.animate(nowMs, playerX, playerZ);
    }
    // Interact detection: inside the gold ring of a primed site.
    this.interactSiteId = null;
    for (const site of this.sites) {
      const state = this.states.get(site.id);
      if (state?.phase !== 'primed') continue;
      const dx = playerX - site.position.x;
      const dz = playerZ - site.position.z;
      const radius =
        site.radii.interactionRadius ?? DEFAULT_DUNGEON_ENTRANCE_RADII.interactionRadius;
      if (dx * dx + dz * dz <= radius * radius) {
        this.interactSiteId = site.id;
        break;
      }
    }
    this.updatePrompt();
  }

  private updatePrompt(): void {
    const wanted = this.interactSiteId !== null && !this.explorer.active;
    if (wanted && !this.prompt) {
      const prompt = document.createElement('div');
      prompt.style.cssText =
        'position:fixed;bottom:88px;left:50%;transform:translateX(-50%);z-index:55;' +
        'background:rgba(11,16,32,0.85);color:#e8d9a0;padding:6px 14px;border:1px solid #c8a23a;' +
        'border-radius:8px;font:13px system-ui;pointer-events:none';
      prompt.textContent = 'Press G — enter the dungeon (techdemo preview)';
      document.body.appendChild(prompt);
      this.prompt = prompt;
    } else if (!wanted && this.prompt) {
      this.prompt.remove();
      this.prompt = null;
    }
  }

  /** Enter the primed dungeon whose ring the player stands in (dev/preview walk). */
  tryEnter(): boolean {
    if (this.explorer.active || this.interactSiteId === null) return false;
    const state = this.states.get(this.interactSiteId);
    if (state?.phase !== 'primed') return false;
    void this.explorer.enter(state.plan, () => undefined);
    return true;
  }

  /** Dev-menu path: prime-and-enter a specific difficulty without walking (item 5). */
  async previewDifficulty(difficultyId: string, cycle?: number): Promise<boolean> {
    const site = this.sites.find((entry) => entry.difficultyId === difficultyId) ?? this.sites[0];
    if (!site) return false;
    // Dev hook only (S4 visual lane): `cycle` re-rolls the site from a dormant
    // state at that generation cycle, so a capture can sample several seeds of
    // one shrine. The cycle is part of the deterministic cycle seed, so the
    // same cycle always yields the same plan.
    if (cycle !== undefined) {
      this.explorer.exit();
      this.states.set(site.id, { phase: 'dormant', siteId: site.id, generationCycle: cycle });
    }
    let state = this.states.get(site.id) as DungeonEntranceRuntimeState;
    if (state.phase !== 'primed') {
      const result = updateDungeonEntranceLifecycle({
        worldSeed: TECHDEMO_WORLD_SEED,
        site,
        state,
        observers: [
          {
            playerId: this.playerId,
            position: { ...site.position },
            partySize: DUNGEON_PREVIEW_PARTY_SIZE,
          },
        ],
        catalog: BUNDLE.catalog,
        shrineProfiles: BUNDLE.shrineProfiles,
        iconRules: BUNDLE.iconRules,
      });
      state = result.state;
      this.states.set(site.id, state);
      this.refreshVisual(site.id);
    }
    if (state.phase !== 'primed') return false;
    await this.explorer.enter(state.plan, () => undefined);
    return true;
  }

  /** Dev hook only (S4): stand the preview walker at a named spot; false if absent. */
  placePreviewAt(spot: PreviewSpot): boolean {
    return this.explorer.placeAt(spot);
  }

  /** Dev hook only (capture): hide banner + capsule walker, walk slowly forward; false if not inside. */
  setPreviewPresentation(on: boolean): boolean {
    return this.explorer.setPresentation(on);
  }

  exitPreview(): void {
    this.explorer.exit();
  }

  /** The entered interior's root (the E3.3.S4 active slot), else null. */
  slotRoot(): THREE.Object3D | null {
    return this.explorer.slotRoot();
  }

  listSites(): Array<{ id: string; difficultyId: string; x: number; z: number; phase: string }> {
    return this.sites.map((site) => ({
      id: site.id,
      difficultyId: site.difficultyId,
      x: site.position.x,
      z: site.position.z,
      phase: this.states.get(site.id)?.phase ?? 'dormant',
    }));
  }

  private refreshVisual(siteId: string): void {
    const site = this.sites.find((entry) => entry.id === siteId);
    const state = this.states.get(siteId);
    const visual = this.visuals.get(siteId);
    if (!site || !state || !visual) return;
    const profile = BUNDLE.shrineProfiles.find(
      (entry) => entry.difficultyId === site.difficultyId,
    ) as DungeonShrineProfile;
    visual.rebuild(planDungeonEntranceScene(site, profile, state));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onInteractKey, true);
    this.prompt?.remove();
    this.prompt = null;
    this.explorer.exit();
    for (const visual of this.visuals.values()) visual.dispose();
    this.scene.remove(this.group);
  }
}
